"use client";

import Image from "next/image";
import {Switch} from "@headlessui/react";
import React, {useEffect, useState} from "react";
import {Table, TableBody, TableCell, TableRoot, TableRow} from "~/components/tremor/Table";
import {FaCheck} from "react-icons/fa";
import clsx from "clsx";


const expirationDateOnReject = Date.now() + 14 * 24 * 60 * 60 * 1000; // 14 Days
const expirationDateOnAcceptAll = Date.now() + 365 * 24 * 60 * 60 * 1000; // 1 Year

export const defaultCookieSettings: CookieConsentSettings = {
    expirationDate: expirationDateOnAcceptAll,
    functionalGeneral: true,
    analyticsGoogle: false,
    analyticsHotjar: false,
    marketingGoogle: false,
    marketingMeta: false,
};

export type CookieConsentSettings = {
    expirationDate: number,
    functionalGeneral: boolean;
    analyticsGoogle: boolean;
    analyticsHotjar: boolean;
    marketingGoogle: boolean;
    marketingMeta: boolean;
};

export type CookieBannerSetting = {
    id: keyof CookieConsentSettings;
    disabled: boolean;
    name: string;
    provider: string;
    purpose: string;
    dataPrivacyUrl: string;
    cookies: string;
    cookieLifetime: string;
};

export function CookieBanner(
    { dictionary, showBanner, settings, submitSettings } : { dictionary: any, showBanner: boolean, settings: CookieConsentSettings, submitSettings: Function }
) {

    const [showBannerSettings, setShowBannerSettings] = useState<boolean>(false);
    const [cookieConsentSettings, setCookieConsentSettings] = useState<CookieConsentSettings>(settings);

    const cookieBannerSettings: CookieBannerSetting[] = [
        {
            id: "functionalGeneral",
            disabled: true,
            name: "Data-privacy setting",
            provider: "8awake GmbH",
            purpose: "Cookie is used for storing your answer on this banner. So that we won't ask you again.",
            dataPrivacyUrl: "https://docid.pro/data-privacy/",
            cookies: "docid-cc",
            cookieLifetime: "180 days",
        },
        {
            id: "analyticsGoogle",
            disabled: false,
            name: "Google Analytics",
            provider: "Google LLC",
            purpose: "Cookies by Google are used for website analytics. Generates statistical data on how the visitor uses the website.",
            dataPrivacyUrl: "https://policies.google.com/privacy?hl=en",
            cookies: "_ga, _gat, _gid",
            cookieLifetime: "2 Years",
        },
        {
            id: "analyticsHotjar",
            disabled: false,
            name: "Hotjar",
            provider: "Hotjar Ltd",
            purpose: "We use Hotjar in order to better understand our users’ needs and to optimize this service and experience. Hotjar is a technology service that helps us better understand our users’ experience (e.g. how much time they spend on which pages, which links they choose to click, what users do and don’t like, etc.) and this enables us to build and maintain our service with user feedback. Hotjar uses cookies and other technologies to collect data on our users’ behavior and their devices. This includes a device's IP address (processed during your session and stored in a de-identified form), device screen size, device type (unique device identifiers), browser information, geographic location (country only), and the preferred language used to display our website. Hotjar stores this information on our behalf in a pseudonymized user profile. Hotjar is contractually forbidden to sell any of the data collected on our behalf.",
            dataPrivacyUrl: "https://www.hotjar.com/legal/policies/privacy/",
            cookies: "Cookies with the prefix _hj",
            cookieLifetime: "1 Year",
        },
        {
            id: "marketingGoogle",
            disabled: false,
            name: "Google Ads",
            provider: "Google LLC",
            purpose: "Cookies by Google Ads are used for advertising and targeting purpose.",
            dataPrivacyUrl: "https://policies.google.com/privacy?hl=en",
            cookies: "__gsas, __gpi, __gpi_optout, NID, DSID, test_cookie, id, __gads",
            cookieLifetime: "2 Years",
        },
        {
            id: "marketingMeta",
            disabled: false,
            name: "Meta",
            provider: "Meta Inc.",
            purpose: "Cookies by Facebook are used for advertising and marketing activities. Generates statistical data on how the visitor uses the website.",
            dataPrivacyUrl: "https://www.facebook.com/policy.php",
            cookies: "_fbp, fr",
            cookieLifetime: "2 Years",
        }
    ];

    const updateCookieSetting = (id: keyof CookieConsentSettings) => {
        setCookieConsentSettings((prev: CookieConsentSettings) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const triggerDataLayerEvents = (settings: CookieConsentSettings) => {

        const updateConsentPreferences = {
            "event": "update_consent_preferences",
            "ad_storage": settings.marketingGoogle ? "granted" : "denied",
            "ad_user_data": settings.marketingGoogle ? "granted" : "denied",
            "ad_personalization": settings.marketingGoogle ? "granted" : "denied",
            "analytics_storage": settings.analyticsGoogle ? "granted" : "denied",
            "functionality_storage": settings.functionalGeneral ? "granted" : "denied",
            "personalization_storage": settings.marketingGoogle ? "granted" : "denied",
            "security_storage": settings.functionalGeneral ? "granted" : "denied",
        };

        /**
         * Update consent manager
         */
        if(window.dataLayer) {
            //console.log("Trigger update_consent_preferences event with", updateConsentPreferences);
            window.dataLayer.push(updateConsentPreferences);
        }

        /**
         * Execute data layer events
         */
        Object.entries(settings).forEach(([key, value]) => {
            if(value) {
                if(window.dataLayer) {
                    //console.log(`Trigger event ${key} event with value`, value);
                    window.dataLayer.push({
                        event: key,
                        [key]: value,
                    });
                }
            }
        });

    };

    const submitCustomCookieSettings = () => {
        const isConsentMissing = Object.values(cookieConsentSettings).some((setting) => !setting);
        submitSettings({
            ...cookieConsentSettings,
            expirationDate: isConsentMissing ? expirationDateOnReject : expirationDateOnAcceptAll,
        });
    };

    const submitDeclineAllCookieSettings = () => {
        submitSettings({
            ...cookieConsentSettings,
            expirationDate: expirationDateOnReject,
        });
    };

    const submitAcceptAllCookieSettings = () => {
        submitSettings({
            expirationDate: expirationDateOnAcceptAll,
            functionalGeneral: true,
            analyticsGoogle: true,
            analyticsHotjar: true,
            marketingGoogle: true,
            marketingMeta: true,
        });
    };

    useEffect(() => {
        triggerDataLayerEvents(settings);
    }, [settings]);

    return (
        <div id="cookie-banner-container" className={clsx(showBanner ? "flex items-end md:items-center justify-center pb-10 md:pb-0" : "hidden", "fixed inset-0 bg-black bg-opacity-40 z-[9999999]")}>
            <div id="cookie-banner" className="flex flex-col gap-4 md:gap-5 bg-white box-border p-6 md:p-10 max-h-[80vh] max-w-[90vw] w-full sm:w-[760px] overflow-y-auto rounded-lg shadow-lg">
                <div className="flex items-center justify-center md:justify-start pb-4 md:pb-5 border border-b-1 border-t-0 border-r-0 border-l-0 border-zinc-100">
                    <div className="w-[130px]"><Image quality={100} width={140} height={40} priority={true} alt="DocID" src="/images/logo-docid.svg" /></div>
                </div>
                <p className="font-bold text-xl md:text-2xl text-center md:text-left">{dictionary.cookieBannerHeadline}</p>
                <p className="font-normal text-sm md:text-base leading-snug">{dictionary.cookieBannerIntro}</p>
                <div className={clsx(showBannerSettings ? "flex" : "hidden", "flex flex-col gap-4")}>
                    {cookieBannerSettings.map((setting: CookieBannerSetting, index) => (
                        <CookieBannerSetting
                            key={`setting-${index}`}
                            dictionary={dictionary}
                            enabled={setting.disabled}
                            handleToggleUpdate={() => updateCookieSetting(setting.id)}
                            settings={setting} />
                    ))}
                    <div className="flex items-end justify-center md:justify-end">
                        <button onClick={() => submitCustomCookieSettings()} className="inline-flex items-center justify-center gap-3 w-full md:w-auto bg-zinc-200 hover:bg-zinc-300 text-zinc-600 md:min-w-52 px-3 md:px-6 py-3 rounded-md text-normal md:text-lg text-center font-jost font-semibold outline-2 outline-offset-2 transition-colors select-none cursor-pointer whitespace-nowrap">
                            {dictionary.cookieBannerButtonConfirmSettings}
                        </button>
                    </div>
                </div>
                <div className="flex flex-col md:flex-row gap-4 items-end justify-center md:justify-end pt-2">
                    <button onClick={() => setShowBannerSettings(!showBannerSettings)} className="inline-flex items-center justify-center gap-3 w-full md:w-auto bg-zinc-200 hover:bg-zinc-300 text-zinc-600 md:min-w-52 px-3 md:px-6 py-3 rounded-md text-normal md:text-lg text-center font-jost font-semibold outline-2 outline-offset-2 transition-colors select-none cursor-pointer whitespace-nowrap">
                        {showBannerSettings ? dictionary.cookieBannerButtonHideSettings : dictionary.cookieBannerButtonShowSettings}
                    </button>
                    <button onClick={() => submitDeclineAllCookieSettings()} className="inline-flex items-center justify-center gap-3 w-full md:w-auto bg-zinc-200 hover:bg-zinc-300 text-zinc-600 md:min-w-52 px-3 md:px-6 py-3 rounded-md text-normal md:text-lg text-center font-jost font-semibold outline-2 outline-offset-2 transition-colors select-none cursor-pointer whitespace-nowrap">
                        {dictionary.cookieBannerButtonDeclineAll}
                    </button>
                    <button onClick={() => submitAcceptAllCookieSettings()} className="inline-flex items-center justify-center gap-3 w-full md:w-auto bg-black hover:bg-black/80 md:min-w-52 px-3 md:px-6 py-3 rounded-md text-normal md:text-lg text-center font-jost font-semibold outline-2 outline-offset-2 transition-colors text-white select-none cursor-pointer">
                        <FaCheck/><span className="whitespace-nowrap">{dictionary.cookieBannerButtonAcceptAll}</span>
                    </button>
                </div>
            </div>
        </div>
    )
}

function CookieBannerSetting(
    {dictionary, enabled, settings, handleToggleUpdate}: { dictionary: any, enabled: boolean, settings: CookieBannerSetting, handleToggleUpdate: Function }
) {

    return (
        <TableRoot className="w-full whitespace-pre-wrap">
            <Table>
                <TableBody>
                    <TableRow>
                        <TableCell className="!px-0 !py-2 text-left text-sm md:text-normal">{dictionary.cookieBannerSettingsLabelAccept}</TableCell>
                        <TableCell className="!px-0 !pl-2 !py-2 text-left text-sm md:text-normal">
                            <div className="inline-flex">
                                <Switch checked={Boolean(enabled)} disabled={settings.disabled} onChange={() => handleToggleUpdate()} className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-zinc-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-grass-400 focus:ring-offset-2 data-[checked]:bg-grass-400">
                                    <span className="pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5">
                                        <span aria-hidden="true" className="absolute inset-0 flex size-full items-center justify-center transition-opacity duration-200 ease-in group-data-[checked]:opacity-0 group-data-[checked]:duration-100 group-data-[checked]:ease-out">
                                            <svg fill="none" viewBox="0 0 12 12" className="h-3 w-3 text-gray-400"><path d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        </span>
                                        <span aria-hidden="true" className="absolute inset-0 flex size-full items-center justify-center opacity-0 transition-opacity duration-100 ease-out group-data-[checked]:opacity-100 group-data-[checked]:duration-200 group-data-[checked]:ease-in">
                                            <svg fill="currentColor" viewBox="0 0 12 12" className="h-3 w-3 text-grass-700">
                                                <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"/>
                                            </svg>
                                        </span>
                                    </span>
                                </Switch>
                            </div>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className="!px-0 !py-2 text-left truncate text-sm md:text-normal">{dictionary.cookieBannerSettingsLabelName}</TableCell>
                        <TableCell className="!px-0 !pl-2 !py-2 text-left text-sm md:text-normal whitespace-pre-wrap leading-snug">{settings.name}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className="!px-0 !py-2 text-left truncate text-sm md:text-normal">{dictionary.cookieBannerSettingsLabelProvider}</TableCell>
                        <TableCell className="!px-0 !pl-2 !py-2 text-left text-sm md:text-normal whitespace-pre-wrap leading-snug">{settings.provider}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className="!px-0 !py-2 text-left truncate text-sm md:text-normal">{dictionary.cookieBannerSettingsLabelPurpose}</TableCell>
                        <TableCell className="!px-0 !pl-2 !py-2 text-left text-sm md:text-normal whitespace-pre-wrap leading-snug">{settings.purpose}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className="!px-0 !py-2 text-left truncate text-sm md:text-normal">{dictionary.cookieBannerSettingsLabelDataPrivacyPolicy}</TableCell>
                        <TableCell className="!px-0 !pl-2 !py-2 text-left text-sm md:text-normal whitespace-nowrap truncate">
                            <a className="underline" href={settings.dataPrivacyUrl} target="_blank">{dictionary.cookieBannerSettingsLabelDataPrivacyPolicyLink}</a>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className="!px-0 !py-2 text-left truncate text-sm md:text-normal">{dictionary.cookieBannerSettingsLabelCookies}</TableCell>
                        <TableCell className="!px-0 !pl-2 !py-2 text-left text-sm md:text-normal whitespace-pre-wrap leading-snug">{settings.cookies}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className="!px-0 !py-2 text-left truncate text-sm md:text-normal">{dictionary.cookieBannerSettingsLabelLifetime}</TableCell>
                        <TableCell className="!px-0 !pl-2 !py-2 text-left text-sm md:text-normal whitespace-pre-wrap leading-snug">{settings.cookieLifetime}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableRoot>
    )
}